import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import AuthCardLayout, { errorBoxStyle, fieldInputStyle, fieldLabelStyle, primaryButtonStyle } from '../components/AuthCardLayout'
import ProfileDetailsFields, { emptyProfileDetails, profileDetailsPayload, validateProfileDetails, type ProfileDetails } from '../components/ProfileDetailsFields'

function CompleteRegistration() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const navigate = useNavigate()
  const { completeRegister } = useAuth()

  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [loadingToken, setLoadingToken] = useState(true)
  const [tokenError, setTokenError] = useState('')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [details, setDetails] = useState<ProfileDetails>(emptyProfileDetails)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!token) {
      setTokenError('Missing verification token. Please start registration again.')
      setLoadingToken(false)
      return
    }

    fetch('/api/v1/auth/verify-pre-registration', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    })
      .then(async res => {
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Invalid or expired token')
        setFirstName(data.data.firstName)
        setEmail(data.data.email)
      })
      .catch(err => {
        setTokenError(err.message)
      })
      .finally(() => {
        setLoadingToken(false)
      })
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    const problem = validateProfileDetails(details)
    if (problem) {
      setError(problem)
      return
    }

    setIsLoading(true)
    const result = await completeRegister({ token, password, ...profileDetailsPayload(details) })
    setIsLoading(false)

    if (result.success) {
      navigate('/dashboard')
    } else {
      setError(result.error || 'Failed to complete registration')
    }
  }

  if (loadingToken) {
    return (
      <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F7FAF7'}}>
        <p style={{color: '#4A5568'}}>Verifying your email... Please wait.</p>
      </div>
    )
  }

  if (tokenError) {
    return (
      <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#F7FAF7', padding: '2rem'}}>
        <div style={{background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '480px', textAlign: 'center', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'}}>
          <h1 style={{color: '#1A202C', marginBottom: '1rem'}}>Link Expired or Invalid</h1>
          <p style={{color: '#4A5568', marginBottom: '1.5rem'}}>{tokenError}</p>
          <Link to="/register" style={{display: 'inline-block', background: '#1B5E20', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '8px', textDecoration: 'none', fontWeight: 600}}>
            Start Over
          </Link>
        </div>
      </div>
    )
  }

  return (
    <AuthCardLayout title={`Thank you, ${firstName}!`} subtitle="Complete the information below to create and enable your igotthatus.com account.">
      {error && <div style={errorBoxStyle}>{error}</div>}

      <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
        <div>
          <label style={fieldLabelStyle}>Email</label>
          <input type="email" value={email} disabled style={{...fieldInputStyle, background: '#F7FAF7', color: 'var(--slate)'}} />
        </div>

        <div>
          <label htmlFor="password" style={fieldLabelStyle}>Password</label>
          <div style={{position: 'relative'}}>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{...fieldInputStyle, padding: '0.75rem 4rem 0.75rem 1rem'}}
              placeholder="Create a password"
              required
            />
            <button
              type="button"
              style={{position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.875rem', color: 'var(--slate-light)', background: 'none', border: 'none', cursor: 'pointer'}}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" style={fieldLabelStyle}>Confirm Password</label>
          <input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={fieldInputStyle} placeholder="Confirm your password" required />
        </div>

        <ProfileDetailsFields values={details} onChange={(patch) => setDetails((d) => ({ ...d, ...patch }))} />

        <button type="submit" disabled={isLoading} style={primaryButtonStyle(isLoading)}>
          {isLoading ? 'Creating account...' : 'Create Account'}
        </button>
      </form>
    </AuthCardLayout>
  )
}

export default CompleteRegistration
